import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/form-control'
import {
  Alert,
  AlertIcon,
  AlertDescription,
  Box,
  Button,
  HStack,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text as ChakraText,
  useBoolean,
  VStack,
  Select,
} from '@chakra-ui/react'
import type { FieldProps, FormikErrors, FormikHelpers } from 'formik'
import { Field, Formik } from 'formik'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import React, { useContext, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import useToast from '../../hooks/useToast'
import type { MediaCollectionOption, MediaLibrarySearchCollectionResponse } from '../../interfaces'
import { MLContext } from '../../providers'
import { CustomMetadataContext } from '../../providers/CustomMetadataProvider'
import CMUploadForm from '../MediaLibrary/Details/CustomMetadataForm/CMUploadForm'
import { getCmFieldValidatorMap } from '../MediaLibrary/NewButton/uploadUtils'
import { mediaCollectionNameRegex } from '../MediaLibrary/utils'
import { MediaLibraryActions } from '../../providers/MLProvider'

const CM_FIELD_PREFIX = `###CM_FIELD_PREFIX${Math.random()}###`.replace('.', '')

interface FormProps {
  name: string
  customMetadataSelect: string
}
interface Props {
  isOpen: boolean
  setMediaCollection?: Dispatch<SetStateAction<MediaCollectionOption[]>>
  onClose(): void
  onOpen?(): void
}

interface CollectionErrorResponse {
  message?: string
  help?: string
  errors?: Array<{ field?: string; error?: string }>
}

const CreateMediaCollection = ({
  isOpen,
  onClose,
  setMediaCollection,
  onOpen,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [, dispatch] = useContext(MLContext)
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const toast = useToast()
  const [showCmForm, setShowCmForm] = useState(false)

  const { cmFields } = useContext(CustomMetadataContext)
  
  useEffect(() => {
    if (isOpen) {
      setShowCmForm(false)
    }
  }, [isOpen])
  const mappedCmFields = useMemo(
    () =>
      cmFields.length
        ? cmFields.map(f => ({
            ...f,
            _name: f.name,
            name: `${CM_FIELD_PREFIX}${f.name}`,
          }))
        : [],
    [cmFields],
  )

  const initialCmValues = useMemo(
    () =>
      Object.fromEntries(
        mappedCmFields.map(f => [f.name, f.schema.defaultValue]),
      ),
    [mappedCmFields],
  )

  const cmFieldValidator = useMemo(() => {
    const mandatoryFieldSet = new Set(
      cmFields.filter(f => f.schema.isValueRequired).map(f => f.name),
    )
    const validatorMap = getCmFieldValidatorMap(cmFields)
    return async (fields: FormProps): Promise<FormikErrors<FormProps>> => {
      const result = await Promise.all(
        Object.entries(fields)
          .filter(([name]) => name.startsWith(CM_FIELD_PREFIX))
          .map(async ([name, value]) => {
            const cmFieldName = name.replace(CM_FIELD_PREFIX, '')
            if (value === null) {
              if (mandatoryFieldSet.has(cmFieldName)) {
                return {
                  isValid: false,
                  fieldName: name,
                  error: 'Mandatory field',
                }
              }
              return { isValid: true, fieldName: name, value }
            }
            const yupSchema = validatorMap.get(cmFieldName)
            if (!yupSchema) {
              throw Error('Validator not found')
            }
            try {
              await yupSchema.validate(value)
              return { isValid: true, fieldName: name, value }
            } catch (err: any) {
              return {
                isValid: false,
                fieldName: name,
                error: err?.errors[0],
              }
            }
          }),
      )
      const errorResult = Object.fromEntries(
        result
          .filter(r => !r.isValid)
          .map(({ fieldName, error }) => [`${fieldName}`, error]),
      )
      return errorResult
    }
  }, [cmFields])

  const onSubmitFunction = async (
    values: FormProps,
    helpers: FormikHelpers<FormProps>,
  ): Promise<void> => {
    const reqBody: {
      name: string
      customMetadata?: { [key: string]: string }
    } = {
      name: values.name,
    }
    const cmEntries = Object.entries({ ...values })
      .filter(([name]) => name.startsWith(CM_FIELD_PREFIX))
      .map(([name, val]) => [name.replace(CM_FIELD_PREFIX, ''), val])
    if (cmEntries.length) {
      reqBody.customMetadata = Object.fromEntries(cmEntries)
    }
    const response = (await request.post(
      '/api/v2/media-collections',
      reqBody,
    )) as MediaLibrarySearchCollectionResponse | CollectionErrorResponse
    if (request.error) {
      const isDuplicateName =
        (response as CollectionErrorResponse).message ===
        `Media collection named ${reqBody.name} already exists.`
      helpers.setErrors({
        ...(isDuplicateName
          ? {
              name: t(
                'error_media_collection_name_aready_exists',
                'Media collection name already exists',
              ),
            }
          : {}),
        ...Object.fromEntries(
          ((response as CollectionErrorResponse).errors ?? [])
            .filter(e => e.field?.length && e.error?.length)
            .map(e => [`${CM_FIELD_PREFIX}${e.field}`, e.error]),
        ),
      })
    } else if (setMediaCollection) {
      onClose()
      setMediaCollection(prevState => [
        {
          label: String(values.name),
          value: (response as MediaLibrarySearchCollectionResponse).mediaCollectionId,
        },
        ...prevState,
      ])
      if (onOpen) {
        onOpen()
      }
      dispatch({
        type: MediaLibraryActions.ADD_TO_RECENT_MEDIA_COLLECTION_LIST,
        payload: [response as MediaLibrarySearchCollectionResponse],
      })
    } else {
      dispatch({
        type: MediaLibraryActions.ADD_TO_RECENT_MEDIA_COLLECTION_LIST,
        payload: [response as MediaLibrarySearchCollectionResponse],
      })
      window.trackEvent({
        action: 'Submit',
        category: 'Media collection',
        label: 'Create',
        value: 1,
      })
      toast({
        description: t(
          'media_collection_created_successfully',
          'Media collection created successfully.',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      onClose()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
      }}
    >
      <ModalOverlay />
      <ModalContent maxW="2xl">
        <Formik<FormProps>
          initialValues={{
            name: t('collection', 'Collection'),
            customMetadataSelect: 'cm_default',
            ...initialCmValues,
          }}
          onSubmit={onSubmitFunction}
          validationSchema={Yup.object().shape({
            name: Yup.string()
              .required('Required')
              .matches(mediaCollectionNameRegex, 'Name cannot have a slash.'),
          })}
          validate={cmFieldValidator}
        >
          {({ isSubmitting, submitForm, isValid, values, handleChange }) => (
            <>
              <ModalHeader>
                {t('create_media_collection', 'Create media collection')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody maxH="lg" overflowY="scroll">
                <VStack spacing={4} alignItems="flex-start">
                  <ChakraText color="appBattleshipGrey" maxWidth="100%">
                    {t(
                      'create_new_collection_description',
                      'A new media collection with the given name will be created.',
                    )}
                  </ChakraText>
                  <Field name="name">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl
                        isInvalid={Boolean(
                          form.errors.name && form.touched.name,
                        )}
                        isRequired
                      >
                        <FormLabel>{t('name', 'Name')}</FormLabel>
                        <Input
                          {...field}
                          placeholder={t(
                            'new_media_collection',
                            'New collection',
                          )}
                        />
                        <FormErrorMessage marginBottom="-2.0408em">
                          {form.errors.name}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <br />
                  <Field name="customMetadataSelect" >
                    {({
                      form,
                    }: FieldProps<string, { customMetadataSelect: string }>) => (
                      <FormControl
                        style={{ maxWidth: "400px" }}
                        marginTop="1em"
                        isInvalid={Boolean(
                          form.errors.customMetadataSelect &&
                          form.touched.customMetadataSelect,
                        )}
                      >
                        <FormLabel
                          htmlFor="customMetadataSelect"
                          fontWeight={700}
                          mb={"16px"}
                        >
                          {t('custom_metadata', 'Custom Metadata')}
                        </FormLabel>
                        <Alert status="info" >
                          <AlertIcon
                            height="1em"
                            width="1em"
                            alignItems="flex-start"
                            marginTop="4px"

                          />
                          <AlertDescription fontSize="sm">
                            {t('custom_metadata_default_alert', 'Default values associated with custom metadata fields\
                             will persist even after the fields are cleared via the form.')}
                          </AlertDescription>
                        </Alert>
                        <Select
                          marginTop={"16px"}
                          name="customMetadataSelect"
                          value={values?.customMetadataSelect}
                          onChange={event => {
                            handleChange(event)
                            setShowCmForm(Boolean(event.target.value === "cm_form"))
                          }}
                        >
                          <option value='cm_default'> {t('set_default_values', 'Set default values')}</option>
                          <option value='cm_form'>{t('show_metadata_form', 'set custom metadata')}</option>
                        </Select>

                        <FormErrorMessage>
                          {form.errors.customMetadataSelect}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  {showCmForm ? (
                    mappedCmFields.length ? (
                      <CMUploadForm
                        cmFields={mappedCmFields}
                        renderFromHeader={false}
                      />
                    ) : (
                      <Alert status="info" minW="auto" spacing="1em">
                        <AlertIcon height="1em" width="1em" />
                        <AlertDescription fontSize="sm">
                          <Trans
                            t={t}
                            i18nKey="no_cmf_to_show_add_some_in_ml_settings"
                          >
                            No custom metadata fields to show. Add some in
                            <Link
                              href="/dashboard/settings/media-library"
                              fontSize="sm"
                            >
                              media library settings
                            </Link>
                            .
                          </Trans>
                        </AlertDescription>
                      </Alert>
                    )
                  ) : null}
                </VStack>
              </ModalBody>
              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button
                  onClick={() => {
                    onClose()
                    if (onOpen) {
                      onOpen()
                    }
                  }}
                  variant="outline"
                >
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting || !isValid}
                  onClick={() => {
                    void submitForm()
                  }}
                >
                  {t('create', 'Create')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default CreateMediaCollection
